import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Field, Leaf, Spinner, ThemeSwitch } from "../components/ui";
import { api, errorMessage, type User } from "../lib/api";
import { navigate, useSession } from "../lib/app";

function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="auth-page">
      <aside className="auth-art">
        <div className="brand">
          <Leaf size={28} /> benleaf
        </div>
        <blockquote>
          Typeset together. <em>Commit</em> as you go.
        </blockquote>
        <div className="formula">\documentclass{'{'}thesis{'}'} % live, collaborative, versioned</div>
      </aside>
      <main className="auth-form">
        <ThemeSwitch className="auth-theme" />
        {children}
      </main>
    </div>
  );
}

export function LoginPage() {
  const { setUser, refresh } = useSession();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.post<{ user: User }>("/api/auth/login", { username, password });
      setUser(res.user);
      await refresh();
      const next = new URLSearchParams(location.search).get("next");
      navigate(next && next.startsWith("/") && !next.startsWith("//") ? next : "/", true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <form className="auth-card" onSubmit={submit}>
        <h1>Sign in</h1>
        <Field label="Username">
          <input autoFocus autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />
        </Field>
        <Field label="Password">
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {error && <div className="error-text">{error}</div>}
        <button className="btn btn-primary" disabled={busy || !username || !password}>
          {busy ? <Spinner /> : "Sign in"}
        </button>
        <p className="muted small">benleaf is invite-only. Ask the admin for an invite link.</p>
      </form>
    </AuthShell>
  );
}

export function InvitePage({ token }: { token: string }) {
  const { setUser, refresh } = useSession();
  const [state, setState] = useState<{
    loading: boolean;
    error?: string;
    reset?: { username: string } | null;
    projects?: Array<{ name: string; role: string }>;
  }>({ loading: true });
  const [form, setForm] = useState({ username: "", displayName: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ reset: { username: string } | null; name: string | null; projects: Array<{ name: string; role: string }> }>(
        `/api/invites/${encodeURIComponent(token)}`,
      )
      .then((r) => {
        setState({ loading: false, reset: r.reset, projects: r.projects });
        // The inviter said who this is for; start with that as their display name.
        if (r.name) setForm((f) => ({ ...f, displayName: f.displayName || r.name! }));
      })
      .catch((err) => setState({ loading: false, error: errorMessage(err) }));
  }, [token]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api.post<{ user: User; projects?: Array<{ id: string }> }>(`/api/invites/${encodeURIComponent(token)}/accept`, form);
      setUser(res.user);
      await refresh();
      // Invited to exactly one project: take them straight into it.
      navigate(res.projects?.length === 1 ? `/project/${res.projects[0].id}` : "/", true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (state.loading)
    return (
      <AuthShell>
        <Spinner size={20} />
      </AuthShell>
    );
  if (state.error)
    return (
      <AuthShell>
        <div className="auth-card">
          <h1>Link not valid</h1>
          <p className="muted">{state.error}</p>
          <button className="btn" onClick={() => navigate("/login")}>
            Go to sign in
          </button>
        </div>
      </AuthShell>
    );

  const reset = state.reset;
  return (
    <AuthShell>
      <form className="auth-card" onSubmit={submit}>
        <h1>{reset ? "Choose a new password" : "Create your account"}</h1>
        {!reset && !!state.projects?.length && (
          <p className="muted" style={{ margin: 0 }}>
            You've been invited to {state.projects.map((p) => p.name).join(", ")}. You'll be able to{" "}
            {state.projects.every((p) => p.role === "viewer") ? "view it" : "edit it"} as soon as your account is set up.
          </p>
        )}
        {reset ? (
          <p className="muted">
            Resetting the password for <b>{reset.username}</b>.
          </p>
        ) : (
          <>
            <Field label="Username" hint="Used to sign in and to share projects with you.">
              <input autoFocus autoComplete="username" value={form.username} onChange={set("username")} />
            </Field>
            <Field label="Display name">
              <input autoComplete="name" value={form.displayName} onChange={set("displayName")} placeholder={form.username} />
            </Field>
            <Field label="Email" hint="Optional. Used as the author email on your commits.">
              <input type="email" autoComplete="email" value={form.email} onChange={set("email")} />
            </Field>
          </>
        )}
        <Field label="Password" hint="At least 10 characters.">
          <input type="password" autoComplete="new-password" value={form.password} onChange={set("password")} />
        </Field>
        <Field label="Confirm password">
          <input type="password" autoComplete="new-password" value={form.confirm} onChange={set("confirm")} />
        </Field>
        {error && <div className="error-text">{error}</div>}
        <button className="btn btn-primary" disabled={busy}>
          {busy ? <Spinner /> : reset ? "Set password" : "Create account"}
        </button>
      </form>
    </AuthShell>
  );
}
